import React, {useEffect, useRef, useState} from "react";
import IDE from "../IDE/IDE";
import {useLoaderData} from "react-router-dom";
import Header from "../IDE/Header";
import {Button, Card, ConfigProvider, Steps, Tour, Typography} from "antd";
import {RedoOutlined, SelectOutlined, SyncOutlined, UndoOutlined} from "@ant-design/icons";
import {EventService} from "../services/eventService";
const { Text, Paragraph } = Typography;

// @ts-ignore
export async function loader({ params }) {
    const userId = params.userId;
    return { userId };
}

const initialText = `Amy's Gauge Swatch
This swatch is knit back and forth.

Gauge: 20 stitches by 20 rows

~ Knitting Instructions ~
Cast On 24 stitches.

Round 1: p24
Round 2: k24

Repeat Rounds 1 and 2, 6 more times.
`

const sweaterPatternText = `Sarah's Sweater Pattern for Sleeves
The sleeve is knit top-down from the shoulder to the wrist.
There is no shaping in the arm (no decreases) until you 
reach the wrist. At the wrist, you will decrease the number
of stitches before ending with 6 rows of one-by-one ribbing.

Gauge: 25 stitches by 25 rows

~ Knitting Instructions ~
Cast On 24 stitches. 

Section 1: Length of sleeve:
Round 1: p24
Round 2: k24

Repeat Rounds 1 and 2, 10 more times.
Round 3: p24

Section 2: Shaping (decreases):
Round 4: (k1, k2tog, k1) repeat 6 times.
Round 5: p18
Round 6: (k1, k2tog) repeat 6 times.
Round 7: p12

Section 3: Cuff:
Round 8: (k1, p1) repeat 6 times.
`

const patternUpdateStep = 3;

export default function TaskTwo() {
    // @ts-ignore
    const { userId } = useLoaderData();
    const eventService = new EventService();
    let rowInspectorRef = useRef(null);

    //Initial Text
    const [patternText, setPatternText] = useState(initialText);
    const [updatedText, setUpdatedText] = useState(false);

    // Timer
    const [timeRemaining, setTimeRemaining] = useState("5 minutes");
    useEffect(() => {
        // Send Initial Event
        const date = new Date();
        eventService.saveEvent(userId, "2", {
            id: date.getTime(),
            type: "TASK_START",
            time: date.toString(),
            value: {
                windowHeight: window.innerHeight,
                widowWidth: window.innerWidth
            }
        });

        setTimeout(() => {
            setTimeRemaining("4 minutes");
        }, 60 * 1000 * 1);

        setTimeout(() => {
            setTimeRemaining("3 minutes");
        }, 60 * 1000 * 2);

        setTimeout(() => {
            setTimeRemaining("2 minutes");
        }, 60 * 1000 * 3);

        setTimeout(() => {
            setTimeRemaining("1 minute");
        }, 60 * 1000 * 4);
    }, []);

    const saveStepChange = (step: string) => {
        const date = new Date();
        eventService.saveEvent(userId, "1", {
            id: date.getTime(),
            type: "STEP_CHANGE",
            time: date.toString(),
            value: step
        });
    }

    // Card Steps
    // Card One Tours
    const [rowInspectorOpen, setRowInspectorOpen] = useState<boolean>(false);
    const rowInspectorTour = [
        {
            title: 'Row Inspector',
            target: () => rowInspectorRef.current
        }
    ]

    // Progress Bar and Card Steps
    const [current, setCurrent] = useState(0);
    const nextStep = () => {
        const step = current + 1;
        if (!updatedText && step === patternUpdateStep) {
            setPatternText(sweaterPatternText);
            setUpdatedText(true)
        }
        saveStepChange(step.toString());
        setCurrent(step);
    };
    const prevStep = () => {
        const step = current - 1;
        saveStepChange(step.toString())
        setCurrent(step);
    };
    const onChangeStep = (value: number) => {
        if (!updatedText && value === patternUpdateStep) {
            setPatternText(sweaterPatternText);
            setUpdatedText(true)
        }

        saveStepChange(value.toString())
        setCurrent(value);
    };

    const steps = [
        {
            shortTitle: 'Summary',
            title: 'StitchUp Summary',
            task: <>
                <Paragraph>
                    In Task 1, we familiarized ourselves with the different parts of StitchUp:
                    <ol>
                        <li key={1}>The <Text strong>Pattern Visual</Text>, where you can preview the knitting pattern.</li>
                        <li key={2}>The <Text strong>Pattern Editor</Text>, where you can view and update the written knitting pattern.</li>
                        <li key={3}>The <ConfigProvider
                            theme={{
                                token: {
                                    colorPrimary: '#E27D60',
                                },
                            }}
                        >
                            <Button
                                style={{margin: "2px"}}
                                type="primary"
                                icon={<SelectOutlined />}
                                onClick={() => setRowInspectorOpen(true)}
                                size="small">Row Inspector
                            </Button>
                        </ConfigProvider>, which allows you to highlight rows of the <Text strong>Pattern Visual</Text> to see
                            what knitting instructions created them.
                        </li>
                        <li key={4}>
                            The <ConfigProvider
                            theme={{
                                token: {
                                    colorPrimary: '#E27D60',
                                },
                            }}
                        >
                            <Button
                                style={{margin: "2px"}}
                                type="primary"
                                icon={<UndoOutlined />}
                                size="small">Undo
                            </Button>
                            <Button
                                style={{margin: "2px"}}
                                type="primary"
                                icon={<RedoOutlined />}
                                size="small">Redo
                            </Button>
                            <Button
                                style={{margin: "2px"}}
                                type="primary"
                                icon={<SyncOutlined />}
                                size="small">Reset
                            </Button>
                        </ConfigProvider> Buttons, which allow you to undo, redo and reset changes to the written pattern.
                        </li>
                    </ol>
                </Paragraph>
                <Tour
                    open={rowInspectorOpen}
                    onClose={() => setRowInspectorOpen(false)}
                    steps={rowInspectorTour}
                    mask={true}
                />
            </>
        },
        {
            shortTitle: 'Gauge',
            title: "Amy's Gauge Swatch",
            task: <>
                <Paragraph>
                    The pattern for Amy's gauge swatch is in the editor.
                </Paragraph>
                <Paragraph>
                    Try updating the gauge of her swatch to see how it will affect the size of Amy's Sweater.
                </Paragraph>
                <Paragraph>Gauge is found on <Text code>Line 4</Text>: <Text code>Gauge: 20 stitches by 20 rows</Text>.
                    You can edit the number of stitches or rows.</Paragraph>
                <Paragraph>Notice how it affects the size of the stitches in the <Text strong>Pattern Visual</Text>.</Paragraph>
            </>
        },
        {
            shortTitle: 'Sweater',
            title: "Pattern Update: Sweater",
            task: <>
                <Paragraph>
                    Amy is ready to start knitting her sweater! She has chosen Sarah's Sweater Pattern.
                </Paragraph>
                <Paragraph>
                    Sarah's Sweater Pattern only contains instructions for sleeves.
                </Paragraph>
                <Paragraph>
                    Each sleeve is knit top-down, from shoulder to wrist, with no shaping (decreases) until you reach the wrist.
                    At the wrist, you will decrease stitches before ending with
                    6 rows of one-by-one ribbing.
                </Paragraph>
                <Paragraph>
                    When you click the "Next" button, the text in
                    the <Text strong>Pattern Editor</Text> will update to Sarah's Sweater Pattern.
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Review',
            title: 'Sweater Pattern: review',
            task: <>
                <Paragraph>
                    Take some time to review the updated pattern and visual
                    in the <Text strong>Pattern Editor</Text> and <Text strong>Pattern Visual</Text>.
                </Paragraph>
                <Paragraph>
                    Take a look at the three sections of the pattern: Sleeve length, Shaping (decreases) and Cuff.
                </Paragraph>
                <Paragraph>
                    Try using the <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#E27D60',
                        },
                    }}
                >
                    <Button
                        style={{margin: "2px"}}
                        type="primary"
                        icon={<SelectOutlined />}
                        onClick={() => setRowInspectorOpen(true)}
                        size="small">Row Inspector
                    </Button>
                </ConfigProvider> and highlight these three sections in the Pattern Visual and see where they are in the <Text strong>Pattern Editor</Text>.
                </Paragraph>
                <Tour
                    open={rowInspectorOpen}
                    onClose={() => setRowInspectorOpen(false)}
                    steps={rowInspectorTour}
                    mask={true}
                />
            </>
        },
        {
            shortTitle: 'Cuff',
            title: 'Sweater Pattern: adding the cuff',
            task: <>
                <Paragraph>
                    You might have noticed, the cuff is not complete!
                </Paragraph>
                <Paragraph>
                    According to the pattern, the sleeve should have 6 rows of 1x1 ribbing (k1, p1), but it only has 1!
                </Paragraph>
                <Paragraph>
                    <Text strong>
                        Your task is to add 5 rows of k1, p1 ribbing to the end of the sleeve.
                    </Text>
                </Paragraph>
                <Paragraph>
                    <Text italic>Note: the last step (#7) contains a list of supported knitting instructions. It might be
                        helpful to review this list to complete this task. As a reminder, you can navigate between steps by
                        either clicking the "Next" and "Previous" buttons.
                    </Text>
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Play',
            title: 'Play: experiment!',
            task: <>
                <Paragraph>
                    Next, you will use the remaining time in Task 2 to build and experiment with StitchUp.
                </Paragraph>
                <Paragraph>
                    You are welcome to do whatever you like! For inspiration, you could:
                    <ul>
                        <li>Change the stitch pattern of the sleeve (make it 1x1 rib or 2x2 rib or add a pattern).</li>
                        <li>Turn the sleeve into a scarf</li>
                        <li>Change the size of the sleeve - make it wider, shorter, thinner.</li>
                    </ul>
                </Paragraph>
                <Paragraph>
                    <Text italic>Note: the last step contains a list of supported knitting instructions, we suggest
                        going to the next step to see what is possible and to get inspired!
                    </Text>
                </Paragraph>
            </>
        },
        {
            shortTitle: 'Inst.',
            title: 'Knitting Instructions: What is possible',
            task: <>
                <Paragraph>
                    The following is a list of knitting instructions that are supported by StitchUp:
                    <ul>
                        <li key={1}>
                            Gauge:<Text code>Gauge: 20 stitches by 20 rows</Text>
                        </li>
                        <li key={2}>
                            Casting on:<Text code>Cast On 24 stitches</Text>
                        </li>
                        <li key={3}>
                            Stitch Rounds:<Text code>Round 5: k1, p2, k1, p2</Text>
                        </li>
                        <li key={4}>
                            Repeating stitches within a round:<Text code>Round 6: (k1, p2) repeat 2 times</Text>
                        </li>
                        <li key={5}>
                            Repeating a round: <Text code>Repeat Round 6, 2 more times</Text>
                        </li>
                        <li key={6}>
                            Repeating multiple rounds: <Text code>Repeat Rounds 1, 2 and 3, 5 more times</Text>
                        </li>
                        <li key={7}>
                            Supported stitches: knit (<Text code>k1</Text>), purl (<Text code>p1</Text>), and decreases (<Text code>k2tog</Text>)
                        </li>
                    </ul>
                </Paragraph>
            </>
        }
    ];

    let actions = [];
    if (current < steps.length - 1) {
        actions.push(<Button type="primary" onClick={() => nextStep()} style={{ float: "right" }}>
            Next
        </Button>);
    }
    if (current > 0) {
        actions.push(<Button style={{ margin: '0 8px', float: "right" }} onClick={() => prevStep()}>
            Previous
        </Button>)
    }
    if (current === steps.length - 2){
        actions.push(<Text>Time Remaining: <Text strong>{timeRemaining}</Text></Text>)
    }
    const items = steps.map((item) => ({ key: item.shortTitle, title: item.shortTitle }));

    return (
        <>
            <Header
                userId={userId}
                taskId="2"
            />
            <IDE
                userId={userId}
                taskId="2"
                initialText={patternText}
                setRowInspectorRef={rowInspectorRef}
            >
                <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#41B3A3',
                        },
                    }}
                >
                    <h2>Steps: Task Two</h2>
                    <div>
                        <Steps
                            size="small"
                            className="step-space"
                            current={current}
                            items={items}
                            onChange={onChangeStep}/>
                    </div>
                    <Card
                        className="card-space"
                        type="inner"
                        size="small"
                        title={steps[current].title}
                        bodyStyle={{height: "240px", overflow: "scroll"}}>
                        {steps[current].task}
                        {actions}
                    </Card>
                </ConfigProvider>
            </IDE>
        </>
    );
}